import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

/**
 * Cronómetro de tiempo jugado de un partido.
 *
 * La marca de inicio se guarda en localStorage cuando el operador pulsa
 * «Empezar partido» (ver `markMatchStarted`). Es una referencia local al
 * dispositivo: precisa para quien lleva la mesa. Para un tiempo compartido
 * entre dispositivos haría falta persistir la hora de inicio en el servidor.
 */

const startKey = (matchId) => `mesa_started_${matchId}`;

/** Marca la hora de inicio del partido (llamar al pulsar «Empezar partido»). */
export function markMatchStarted(matchId) {
  if (!matchId) return;
  try {
    localStorage.setItem(startKey(matchId), String(Date.now()));
  } catch {
    /* almacenamiento no disponible: el cronómetro contará desde la carga */
  }
}

/** Borra la marca de inicio (opcional, p. ej. al finalizar definitivamente). */
export function clearMatchStarted(matchId) {
  if (!matchId) return;
  try {
    localStorage.removeItem(startKey(matchId));
  } catch {
    /* sin efecto */
  }
}

function readStart(matchId) {
  try {
    const raw = localStorage.getItem(startKey(matchId));
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  } catch {
    /* ignorar */
  }
  return null;
}

function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function MatchTimer({ matchId, running, sx }) {
  const [, tick] = useState(0);
  const startRef = useRef(null);

  useEffect(() => {
    if (!running || !matchId) {
      startRef.current = null;
      return undefined;
    }
    let start = readStart(matchId);
    if (start == null) {
      // El partido ya estaba en juego sin marca local: empezamos a contar ahora.
      start = Date.now();
      try {
        localStorage.setItem(startKey(matchId), String(start));
      } catch {
        /* ignorar */
      }
    }
    startRef.current = start;
    tick((n) => n + 1);
    const interval = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, [matchId, running]);

  if (!running || startRef.current == null) return null;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 800,
        color: '#0f6b5c',
        ...sx,
      }}
    >
      <AccessTimeIcon sx={{ fontSize: '1em' }} />
      {formatElapsed(Date.now() - startRef.current)}
    </Box>
  );
}
